import { Navigate, useLocation } from "react-router-dom";
import { Loader2, ShieldOff } from "lucide-react";
import PageShell from "@/components/PageShell";
import { useAuth } from "@/context/AuthContext";

/**
 * Guards a private route.
 * - While auth is resolving → spinner (avoids a flash of blank/redirect).
 * - Unauthenticated → redirect to /login?redirect=<intended path>.
 * - requireAdmin + non-admin → 403 (Accès refusé).
 */
export const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <PageShell testId="protected-loading">
        <div className="flex items-center justify-center py-32">
          <Loader2 size={28} className="animate-spin text-gold-ink" />
        </div>
      </PageShell>
    );
  }

  if (!user) {
    const target = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${target}`} replace />;
  }

  if (requireAdmin && !user.is_admin) {
    return (
      <PageShell testId="protected-forbidden">
        <div data-testid="forbidden-403" className="max-w-md mx-auto px-5 py-32 text-center">
          <ShieldOff size={48} className="text-danger-ink mx-auto mb-6" />
          <p className="text-xs tracking-[0.3em] uppercase text-danger-ink mb-3">Erreur 403</p>
          <h1 className="font-serif text-3xl mb-3">Accès refusé</h1>
          <p className="text-sm text-ink-muted">
            Votre compte ne dispose pas des droits administrateur nécessaires
            pour accéder à cette page.
          </p>
        </div>
      </PageShell>
    );
  }

  return children;
};

export default ProtectedRoute;
